import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, isValidElement, useState } from 'react';
import { alpha, Box, Chip, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatPickupTimeLabel } from '@application/utilities/pickup-location-utils';
const VISIBLE_COMPANY_COUNT = 2;
const TouchFriendlyTooltip = ({ title, children, placement = 'top', interactive = false }) => {
    const isTouch = useMediaQuery('(hover: none)');
    const [open, setOpen] = useState(false);
    if (!isValidElement(children)) {
        return children;
    }
    const handleToggle = (event) => {
        if (!isTouch)
            return;
        event.stopPropagation();
        event.preventDefault();
        setOpen((prev) => !prev);
    };
    return (_jsx(Tooltip, { arrow: true, placement: placement, title: title, disableInteractive: !interactive, open: isTouch ? open : undefined, disableHoverListener: isTouch, disableFocusListener: isTouch, disableTouchListener: true, onClose: () => setOpen(false), slotProps: {
            popper: isTouch
                ? {
                    onClick: (event) => event.stopPropagation(),
                    onMouseDown: (event) => event.stopPropagation(),
                    onTouchStart: (event) => event.stopPropagation()
                }
                : interactive
                    ? {
                        onWheel: (event) => event.stopPropagation()
                    }
                    : undefined,
            tooltip: interactive
                ? {
                    sx: {
                        maxHeight: 280,
                        overflowY: 'auto',
                        pointerEvents: 'auto'
                    },
                    onWheel: (event) => event.stopPropagation()
                }
                : undefined
        }, children: cloneElement(children, {
            onClick: (event) => {
                children.props.onClick?.(event);
                handleToggle(event);
            },
            onMouseDown: (event) => {
                children.props.onMouseDown?.(event);
                if (isTouch)
                    event.stopPropagation();
            },
            onTouchStart: (event) => {
                children.props.onTouchStart?.(event);
                if (isTouch)
                    event.stopPropagation();
            }
        }) }));
};
export const PickupLocationOptionRow = ({ option }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const pickupTime = formatPickupTimeLabel(option.timeOfPickup);
    const companies = option.companies ?? [];
    const visibleCompanies = companies.slice(0, VISIBLE_COMPANY_COUNT);
    const hiddenCount = Math.max(companies.length - VISIBLE_COMPANY_COUNT, 0);
    const hiddenCompaniesTitle = (_jsx(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            py: 0.25
        }, children: companies.slice(VISIBLE_COMPANY_COUNT).map((company) => (_jsx(Typography, { sx: { fontSize: 12, lineHeight: 1.4 }, children: company.name }, company.id))) }));
    return (_jsxs(Box, { sx: { width: '100%', py: 0.75 }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1
                }, children: [_jsx(TouchFriendlyTooltip, { title: option.label, children: _jsx(Typography, { sx: {
                                color: '#00244D',
                                fontSize: 14,
                                minWidth: 0,
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer'
                            }, children: option.label }) }), pickupTime && (_jsx(Chip, { size: 'small', color: 'info', variant: 'soft', label: pickupTime, sx: {
                            flexShrink: 0,
                            height: 22,
                            fontSize: 12,
                            fontWeight: 600
                        } }))] }), _jsxs(Box, { sx: {
                    mt: 0.75,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5
                }, children: [visibleCompanies.map((company) => (_jsx(TouchFriendlyTooltip, { title: company.name, children: _jsx(Chip, { size: 'small', color: 'default', variant: 'soft', label: company.name, sx: {
                                maxWidth: 180,
                                height: 22,
                                fontSize: 11,
                                cursor: 'pointer',
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }
                            } }) }, company.id))), hiddenCount > 0 && (_jsx(TouchFriendlyTooltip, { title: hiddenCompaniesTitle, interactive: true, children: _jsx(Chip, { size: 'small', label: t('familyForm.moreCompanies', {
                                count: hiddenCount,
                                defaultValue: `+${hiddenCount} more`
                            }), sx: {
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.16)
                                }
                            } }) }))] })] }));
};
